/**
 * Application AGORA : fournisseurs (configuration, langue, retours à l'utilisateur, données, session).
 *   - retours (retours/Retours.tsx) : zone des messages éphémères et fenêtre de confirmation, communes à tous les écrans ;
 *   - /connexion, /auth/retour et /verification/retour (fenêtre de vérification d'identité) : routeur lié à l'historique
 *     du navigateur (pages/navigateur.ts) ;
 *   - application connectée : socle puis espace de travail (pages ouvertes, chacune avec son propre routeur –
 *     routes dans pages/Volets.tsx). Pas de BrowserRouter englobant.
 */
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { ROUTE_VERIFICATION_RETOUR } from '@agora/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Button, StatusBadge } from '../design';
import type { ConfigApp } from './config';
import { ConfigProvider, SocleProvider } from './contexte';
import { LangueProvider, useT } from './i18n';
import { SessionProvider, useSession } from './auth/session';
import { ErreurApi } from './api/client';
import { Coquille } from './shell/Coquille';
import { EcranConnexion, RetourAuth, memoriserDestination } from './ecrans/Connexion';
import { EspaceProvider } from './pages/espace';
import { RouteurNavigateur, adresseCourante, naviguerNavigateur, useRouteAuth } from './pages/navigateur';
import { RetoursProvider } from './retours/Retours';
import { FinVerification, RetourVerification } from './verification/verification';

const NON_REESSAYABLES = new Set(['non_authentifie', 'interdit', 'separation_taches', 'introuvable', 'validation', 'conflit', 'etat_incompatible', 'module_inactif', 'depot_differe', 'depot_modifie']);

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // Base Aurora en reprise (base_indisponible) ou réseau : nouvelles tentatives espacées ; erreurs métier : aucune.
      retry: (n, e) => !(e instanceof ErreurApi && NON_REESSAYABLES.has(e.code)) && n < 3,
      retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
      refetchOnWindowFocus: false,
    },
  },
});

function EcranPlein({ children }: { children: React.ReactNode }) {
  return <div className="ag-screen ag-canvas agx-plein agx-centre">{children}</div>;
}

function Chargement() {
  const { t } = useT();
  return <EcranPlein><StatusBadge status="progress" label={t('app.chargement')} /></EcranPlein>;
}

function ErreurSocle({ erreur, reessayer }: { erreur: unknown; reessayer: () => void }) {
  const { t } = useT();
  const { fermer } = useSession();
  const code = erreur instanceof ErreurApi ? erreur.code : 'interne';
  return (
    <EcranPlein>
      <section className="ag-glass-card agx-carte agx-carte-etroite" role="alert">
        <StatusBadge status="danger" label={t(`erreurs.${code}`)} />
        <div className="agx-actions">
          <Button variant="quiet" onClick={() => void fermer()}>{t('profil.deconnexion')}</Button>
          <Button variant="primary" icon="sync" onClick={reessayer}>{t('commun.reessayer')}</Button>
        </div>
      </section>
    </EcranPlein>
  );
}

/** Application connectée : session Cognito requise, puis socle (utilisateur, préférences, catalogue) et espace. */
function Protege() {
  const { etat } = useSession();
  React.useEffect(() => {
    if (etat !== 'deconnecte') return;
    memoriserDestination(adresseCourante());
    naviguerNavigateur('/connexion', true);
  }, [etat]);
  if (etat !== 'connecte') return <Chargement />;
  return (
    <SocleProvider chargement={<Chargement />} erreur={(e, r) => <ErreurSocle erreur={e} reessayer={r} />}>
      <EspaceProvider>
        <Coquille />
        <FinVerification />
      </EspaceProvider>
    </SocleProvider>
  );
}

function Racine() {
  const auth = useRouteAuth();
  if (!auth) return <Protege />;
  return (
    <RouteurNavigateur>
      <Routes>
        <Route path="/connexion" element={<EcranConnexion />} />
        <Route path="/auth/retour" element={<RetourAuth />} />
        <Route path={ROUTE_VERIFICATION_RETOUR} element={<RetourVerification />} />
      </Routes>
    </RouteurNavigateur>
  );
}

export function App({ config }: { config: ConfigApp }) {
  return (
    <React.StrictMode>
      <ConfigProvider value={config}>
        <LangueProvider>
          <RetoursProvider>
            <QueryClientProvider client={client}>
              <SessionProvider config={config}>
                <Racine />
              </SessionProvider>
            </QueryClientProvider>
          </RetoursProvider>
        </LangueProvider>
      </ConfigProvider>
    </React.StrictMode>
  );
}
